/**
 * UI-only feature gating (menus, routes, FeatureGate).
 *
 * SECURITY WARNINGS:
 * - H-2: Permissions cached in localStorage are for presentation only
 * - H-3: PII (email, company_name) should NOT be stored in localStorage (GDPR violation)
 * - Authorization is enforced by backend on every API request
 * - Tampering with client storage must not grant API access
 * - Server-side RBAC is the security control (accepted risk)
 *
 * SECURITY FIX H-3: Store only minimal non-PII data in localStorage.
 * Email and company_name should be fetched from server when needed or stored in sessionStorage.
 */
import PropTypes from "prop-types";
import { LOCAL_STORAGE_KEYS } from "./localStorageUtil";
import { eraseAuthCookies } from "./cookieUtil";

const LEGACY_PERMISSIONS_KEY = "USER_PERMISSIONS";
const LEGACY_USER_DETAILS_KEY = "IQEVSE_FLEET_CSMS_user_details";

export const FEATURES = {
  FLEET_SEARCH: "550e8400e29b41d4",
  FLEET_EXPORT: "550e8400e29b41d5",
  CHARGER_METRICS: "550e8400e29b41d6",
  CHARGER_SUMMARY: "550e8400e29b41d7",
  CHARGER_REMOTE_START: "550e8400e29b41d8",
  CHARGER_REMOTE_STOP: "550e8400e29b41d9",
  OCPP_MESSAGES: "550e8400e29b41da",
  TROUBLESHOOTING: "550e8400e29b41db",
  USER_MANAGEMENT: "550e8400e29b41dc",
  SYSTEM_SETTINGS: "550e8400e29b41dd",
  COMMANDS: "550e8400e29b41de",
};
export const ROLE = {
  ADMIN: "ADMIN",
  READ: "READ",
  CUSTOM: "CUSTOM",
};

function migrateLegacyPermissions() {
  try {
    const legacy = localStorage.getItem(LEGACY_PERMISSIONS_KEY);
    if (!legacy) return null;
    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS)) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS, legacy);
    }
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
    return JSON.parse(legacy);
  } catch {
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
    return null;
  }
}

function readStoredPermissions() {
  migrateLegacyPermissions();
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS);
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS);
    return null;
  }
}

function removeStoredPermissions() {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS);
  localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
}

/** Removes legacy user-details keys only (H-2); does not clear FEATURE_ACCESS. */
export function clearLegacyUserDetailsStorage() {
  try {
    localStorage.removeItem(LEGACY_USER_DETAILS_KEY);
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
  } catch {
    // ignore
  }
}

/**
 * SECURITY FIX H-5: Safe redirect with validation to prevent open redirect attacks
 *
 * @param {string} returnUrl - Optional return URL (must be relative)
 */
function safeRedirect(url) {
  // Only allow relative URLs
  if (!url || !url.startsWith("/")) {
    return "/login";
  }

  // Block protocol-relative URLs (//evil.com)
  if (url.startsWith("//")) {
    return "/login";
  }

  // Block data: and javascript: URLs
  if (url.match(/^(data|javascript|vbscript):/i)) {
    return "/login";
  }

  // Block encoded attempts
  if (url.includes("%2F%2F") || url.includes("%3A")) {
    return "/login";
  }

  return url;
}

export function redirectToLogin(returnUrl) {
  if (!window.location.pathname.startsWith("/login")) {
    const safeUrl = returnUrl ? safeRedirect(returnUrl) : "/login";
    window.location.href = safeUrl;
  }
}

class FeatureManager {
  constructor() {
    this.permissions = null;
  }

  init(loginResponse) {
    this.permissions = loginResponse.user_permissions;
    if (this.permissions) {
      // SECURITY FIX H-3: Store only non-PII data in localStorage
      const minimalData = {
        role: this.permissions.role,
        allowed_features: this.permissions.allowed_features || [],
        denied_features: this.permissions.denied_features || [],
      };
      localStorage.setItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS, JSON.stringify(minimalData));

      // Store PII in sessionStorage (cleared on tab close)
      if (
        this.permissions.email ||
        this.permissions.company_name ||
        this.permissions.organization ||
        this.permissions.name
      ) {
        const piiData = {
          email: this.permissions.email || "",
          company_name: this.permissions.company_name || this.permissions.organization || "",
          name: this.permissions.name || "",
        };
        sessionStorage.setItem("USER_PII", JSON.stringify(piiData));
      }
    }
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
  }

  load() {
    if (this.permissions) return;
    this.permissions = readStoredPermissions();
  }

  isAllowed(featureId) {
    if (!this.permissions) this.load();
    if (!this.permissions) {
      this.handleMissingPermissions();
      return false;
    }

    const { allowed_features = [], denied_features = [], role } = this.permissions;

    if (featureId === FEATURES.USER_MANAGEMENT || featureId === FEATURES.SYSTEM_SETTINGS) {
      return role === ROLE.ADMIN;
    }

    if (
      role?.toUpperCase() === ROLE.READ &&
      (featureId === FEATURES.TROUBLESHOOTING || featureId === FEATURES.COMMANDS)
    ) {
      return false;
    }

    if (denied_features.includes(featureId)) return false;
    if (allowed_features.length === 0) return true;
    return allowed_features.includes(featureId);
  }

  isAdmin() {
    if (!this.permissions) this.load();
    if (!this.permissions) {
      this.handleMissingPermissions();
      return false;
    }
    return this.permissions?.role === ROLE.ADMIN;
  }

  clear() {
    this.permissions = null;
    removeStoredPermissions();
  }

  handleMissingPermissions() {
    this.clear();
    redirectToLogin();
  }
}

const featureManager = new FeatureManager();

export const initFeatureAccess = loginResponse => featureManager.init(loginResponse);
export const clearFeatureAccess = () => featureManager.clear();
export const checkFeatureAccess = featureId => featureManager.isAllowed(featureId);

export function getSessionProfile() {
  // SECURITY FIX H-3: Read PII from sessionStorage (not localStorage)
  try {
    const piiData = sessionStorage.getItem("USER_PII");
    if (piiData) {
      const parsed = JSON.parse(piiData);
      return {
        email: parsed.email || "",
        companyName: parsed.company_name || "My Company",
        displayName: parsed.name || parsed.email?.split("@")[0] || "User",
      };
    }
  } catch {
    // Fall back to default
  }

  return { email: "", companyName: "My Company", displayName: "User" };
}

export function getCompanyDisplayName() {
  return getSessionProfile().companyName;
}

export function isEnphaseUser() {
  // SECURITY FIX H-3: Read company from sessionStorage
  try {
    const piiData = sessionStorage.getItem("USER_PII");
    if (piiData) {
      const parsed = JSON.parse(piiData);
      return (parsed.company_name || "").toLowerCase() === "enphase";
    }
  } catch {
    // Fall back to false
  }
  return false;
}

export const useFeatureAccess = () => ({
  isAllowed: featureId => featureManager.isAllowed(featureId),
  isAdmin: () => featureManager.isAdmin(),
});

export function handleUnauthorizedSession() {
  featureManager.clear();
  clearLegacyUserDetailsStorage();
  eraseAuthCookies();
  // SECURITY FIX H-3: Clear PII from sessionStorage
  sessionStorage.removeItem("USER_PII");
  redirectToLogin();
}

export const FeatureGate = ({ featureId, children }) => {
  const { isAllowed } = useFeatureAccess();
  return isAllowed(featureId) ? children : null;
};

FeatureGate.propTypes = {
  featureId: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default featureManager;
